(function () {
  "use strict";

  const parser = window.BulkColorParser;
  if (!parser) {
    console.error("[BulkColorAddToCart] Parser module did not load.");
    return;
  }
  const { buildCartUpdatePayload, buildCartUpdates, buildVariantIndex, cartQuantityForVariant, parseAiCommands,
    parseCartCommands, planCartCommands } = parser;
  const refillSets = Array.isArray(window.BulkColorRefillSets) ? window.BulkColorRefillSets : [];

  const SIMPLE_WORDS = new Set([
    "x", "pack", "packs", "bag", "bags", "packet", "packets", "package", "packages",
    "bolsa", "bolsas", "paquete", "paquetes", "saco", "sacos", "sachet", "sachets",
    "beutel", "packung", "packungen", "bead", "beads", "pc", "pcs", "piece", "pieces",
    "perle", "perles", "cuenta", "cuentas", "conta", "contas", "stuck", "stueck", "stück",
    "each", "cada", "chaque", "chacun", "chacune", "je", "pro", "to", "through", "thru",
    "add", "more", "set", "change", "update", "remove", "delete",
    "包", "袋", "包み", "袋分", "颗", "顆", "粒", "个", "個", "各", "每个", "每個",
    "それぞれ", "各自", "봉", "팩", "각각", "كيس", "اكياس", "أكياس", "عبوة", "عبوات",
    "حبة", "حبات", "خرزة", "خرزات", "كل"
  ]);
  const STANDARD_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
  const HEIC_IMAGE_TYPES = new Set(["image/heic", "image/heif"]);
  const HEIC_DECODER_FALLBACK_URL =
    "https://cdn.jsdelivr.net/npm/libheif-js@1.19.8/libheif-wasm/libheif-bundle.js";
  const isHeicFile = (file) => HEIC_IMAGE_TYPES.has(String(file?.type || "").toLowerCase()) ||
    /\.(?:heic|heif)$/i.test(String(file?.name || ""));
  const isSupportedImage = (file) => STANDARD_IMAGE_TYPES.has(String(file?.type || "").toLowerCase()) || isHeicFile(file);
  let heicDecoderPromise = null;

  const appendHeicDecoderScript = (url) => new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`HEIC decoder script failed to load from ${url}`));
    };
    document.head.appendChild(script);
  });

  const loadHeicDecoder = (themeAssetUrl) => {
    if (heicDecoderPromise) return heicDecoderPromise;
    const initialize = async (exported) => {
      const module = typeof exported === "function" ? await exported() : exported;
      if (!module?.HeifDecoder) throw new Error("HEIC decoder did not initialize.");
      return module;
    };
    if (window.libheif) {
      heicDecoderPromise = initialize(window.libheif).catch((error) => { heicDecoderPromise = null; throw error; });
      return heicDecoderPromise;
    }
    const urls = [...new Set([themeAssetUrl, HEIC_DECODER_FALLBACK_URL].filter(Boolean))];
    heicDecoderPromise = (async () => {
      let lastError = null;
      for (const url of urls) {
        try {
          await appendHeicDecoderScript(url);
          return await initialize(window.libheif);
        } catch (error) {
          lastError = error;
          console.warn("[BulkColorAddToCart] HEIC decoder source failed.", { url, error });
        }
      }
      throw lastError || new Error("HEIC decoder is not configured.");
    })().catch((error) => {
      heicDecoderPromise = null;
      throw new Error("HEIC decoder could not be loaded. Please try again.", { cause: error });
    });
    return heicDecoderPromise;
  };

  const readJson = (root, selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || ""); } catch { return fallback; }
  };
  const cartRoot = () => {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  };
  const cartRequest = async (path, options = {}) => {
    const response = await fetch(`${cartRoot()}cart${path ? `/${path}` : ""}.js`, options);
    if (!response.ok) {
      let message = `Cart request failed with status ${response.status}.`;
      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch {}
      throw new Error(message);
    }
    return response.json();
  };
  const getCart = () => cartRequest("", { method: "GET", headers: { Accept: "application/json" } });
  const updateCart = (updates, attribution = {}) => cartRequest("update", { method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(buildCartUpdatePayload(updates, new Date().toISOString(), attribution)) });
  const replaceDawnSection = (targetId, html, sourceSelector = ".shopify-section") => {
    if (!html) return false;
    const target = document.getElementById(targetId);
    const source = new DOMParser().parseFromString(html, "text/html").querySelector(sourceSelector);
    if (!target || !source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  };
  const renderDawnCartUi = (cartData) => {
    const sections = cartData?.sections;
    if (!sections || typeof sections !== "object") return;
    let drawerRendered = false;
    const drawer = document.querySelector("cart-drawer");
    if (drawer && typeof drawer.renderContents === "function" && sections["cart-drawer"] &&
        sections["cart-icon-bubble"]) {
      try {
        drawer.renderContents(cartData);
        drawerRendered = true;
      } catch (error) {
        console.warn("[BulkColorAddToCart] Dawn cart drawer could not be refreshed.", error);
      }
    }
    if (!drawerRendered) {
      try { replaceDawnSection("cart-icon-bubble", sections["cart-icon-bubble"]); } catch (error) {
        console.warn("[BulkColorAddToCart] Dawn cart icon could not be refreshed.", error);
      }
    }
    try { replaceDawnSection("cart-live-region-text", sections["cart-live-region-text"]); } catch (error) {
      console.warn("[BulkColorAddToCart] Dawn cart live region could not be refreshed.", error);
    }
  };
  const publishDawnCartUpdate = (cartData) => {
    try {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: "bulk-order", cartData })).catch((error) => {
          console.warn("[BulkColorAddToCart] Dawn cart update subscriber failed.", error);
        });
      }
    } catch (error) {
      console.warn("[BulkColorAddToCart] Dawn cart update event could not be published.", error);
    }
  };
  const formatTemplate = (template, values) =>
    String(template || "").replace(/\{(\w+)\}/g, (_, key) => values[key] ?? "");
  const escapeHtml = (value) =>
    String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  const foldWord = (value) => String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const containsNaturalLanguage = (text) => {
    const residue = String(text || "").normalize("NFKC")
      .replace(/[A-Za-z]{1,3}\s*[-_]?\s*[0-9٠-٩۰-۹]{1,4}/g, " ")
      .replace(/[0-9٠-٩۰-۹一二两三四五六七八九十百]+/g, " ")
      .replace(/[xX*×✕:：,，、;；/\\\-–—~]/g, " ");
    const words = residue.match(/\p{L}+/gu) || [];
    return words.some((word) => !SIMPLE_WORDS.has(foldWord(word)));
  };

  const initBulkColor = (root) => {
    if (root.dataset.bulkColorInitialized === "true") return;
    root.dataset.bulkColorInitialized = "true";

    const packSize = Number(root.dataset.packSize) || 1000;
    const aiParseEndpoint = String(root.dataset.aiParseEndpoint || "").trim();
    const chatEndpoint = String(root.dataset.chatEndpoint || "").trim();
    const ocrEndpoint = String(root.dataset.ocrEndpoint || "").trim();
    const heicDecoderUrl = String(root.dataset.heicDecoderUrl || "").trim();
    const turnstileEnabled = root.dataset.turnstileEnabled !== "false";
    const turnstileSiteKey = String(root.dataset.turnstileSiteKey || "").trim();
    const aiEnabled = Boolean(aiParseEndpoint && (!turnstileEnabled || turnstileSiteKey));
    const chatEnabled = Boolean(chatEndpoint && (!turnstileEnabled || turnstileSiteKey));
    const ocrEnabled = Boolean(ocrEndpoint && (!turnstileEnabled || turnstileSiteKey));
    const protectedRequestEnabled = aiEnabled || chatEnabled || ocrEnabled;
    const i18n = readJson(root, "[data-bulk-color-i18n]", {});
    const variants = readJson(root, "[data-bulk-color-variants]", []);
    const variantIndex = buildVariantIndex(variants);
    const validKeys = new Set(variantIndex.keys());
    const variantById = new Map();
    variantIndex.forEach((variant, key) => {
      if (!variantById.has(String(variant.variantId))) variantById.set(String(variant.variantId), { ...variant, key });
    });
    const form = root.querySelector("[data-bulk-color-form]");
    const input = root.querySelector("[data-bulk-color-input]");
    const sendButton = root.querySelector("[data-bulk-color-send]");
    const attachButton = root.querySelector("[data-bulk-color-attach]");
    const imageInput = root.querySelector("[data-bulk-color-image-input]");
    const imagePreview = root.querySelector("[data-bulk-color-image-preview]");
    const imageThumbnail = root.querySelector("[data-bulk-color-image-thumbnail]");
    const imageRemove = root.querySelector("[data-bulk-color-image-remove]");
    const chat = root.querySelector("[data-bulk-color-chat]");
    const transcript = root.querySelector("[data-bulk-color-transcript]");
    const turnstileContainer = root.querySelector("[data-bulk-color-turnstile]");
    const refillOpen = root.querySelector("[data-bulk-color-refill-open]");
    const refillDialog = root.querySelector("[data-bulk-color-refill-dialog]");
    const refillClose = root.querySelector("[data-bulk-color-refill-close]");
    let activeTurn = null;
    let inFlight = false;
    let aiController = null;
    let turnstileWidgetId = null;
    let turnstileToken = "";
    let turnstilePrime = null;
    let turnstileGeneration = 0;
    let selectedImage = null;
    let imagePreparing = false;
    let conversationHistory = [];
    let activeOrderState = [];
    let hasPendingOrder = false;

    if (!form || !input || !sendButton || !attachButton || !imageInput || !imagePreview || !imageThumbnail ||
        !imageRemove || !chat || !transcript || !turnstileContainer) {
      console.warn("[BulkColorAddToCart] Missing required DOM nodes. Check that the section markup was copied completely.");
      return;
    }

    const scrollToLatest = () => window.requestAnimationFrame(() => {
      transcript.lastElementChild?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    try {
      const savedHistory = JSON.parse(window.sessionStorage.getItem("omamoriBulkColorConversationV1") || "[]");
      if (Array.isArray(savedHistory)) conversationHistory = savedHistory.slice(-10);
    } catch {}
    const rememberConversation = (userMessage, assistantMessage) => {
      if (userMessage) conversationHistory.push({ role: "user", content: String(userMessage).slice(0, 2000) });
      if (assistantMessage) conversationHistory.push({ role: "assistant", content: String(assistantMessage).slice(0, 2000) });
      conversationHistory = conversationHistory.slice(-10);
      try { window.sessionStorage.setItem("omamoriBulkColorConversationV1", JSON.stringify(conversationHistory)); } catch {}
    };
    const cartSummary = (cart) => {
      const summary = [];
      variantById.forEach((variant, variantId) => {
        const quantity = cartQuantityForVariant(cart, variantId);
        if (quantity > 0) summary.push({ color_code: variant.key, quantity_bags: quantity, variant_id: variantId });
      });
      return summary;
    };
    const setPendingOrderFromRows = (rows, cart) => {
      const targets = new Map(cartSummary(cart).map((item) => [item.color_code, item.quantity_bags]));
      rows.filter((row) => row.variant && !row.needsReview).forEach((row) => {
        const quantity = Number(row.quantity);
        if (Number.isInteger(quantity) && quantity > 0) targets.set(row.key, quantity);
        else if (quantity === 0) targets.delete(row.key);
      });
      activeOrderState = [...targets.entries()].map(([color_code, quantity_bags]) => ({ color_code, quantity_bags }));
      hasPendingOrder = true;
    };
    const resizeInput = () => {
      input.style.height = "auto";
      input.style.height = `${Math.min(input.scrollHeight, 180)}px`;
    };
    const updateSendState = () => {
      sendButton.disabled = inFlight || imagePreparing || (!input.value.trim() && !selectedImage);
      attachButton.disabled = inFlight || imagePreparing || !ocrEnabled;
    };
    const appendUserTurn = (text, imageUrl = "") => {
      const turn = document.createElement("div");
      turn.className = "bulk-color__turn bulk-color__turn--user";
      const bubble = document.createElement("div");
      bubble.className = "bulk-color__bubble";
      if (imageUrl) {
        const image = document.createElement("img");
        image.className = "bulk-color__user-image";
        image.src = imageUrl;
        image.alt = i18n.imageAttached || "Attached color list";
        if (imageUrl.startsWith("blob:")) {
          image.addEventListener("load", () => URL.revokeObjectURL(imageUrl), { once: true });
        }
        bubble.appendChild(image);
      }
      if (text) {
        const copy = document.createElement("div");
        copy.textContent = text;
        bubble.appendChild(copy);
      }
      turn.appendChild(bubble);
      transcript.appendChild(turn);
    };

    const renderSelectedImage = () => {
      imagePreview.hidden = !selectedImage;
      imageThumbnail.src = selectedImage?.previewUrl || "";
      imageThumbnail.alt = selectedImage ? (i18n.imageAttached || "Attached color list") : "";
    };
    const clearSelectedImage = (revoke = true) => {
      if (revoke && selectedImage?.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(selectedImage.previewUrl);
      selectedImage = null;
      imageInput.value = "";
      renderSelectedImage();
      updateSendState();
    };
    const imageByteLength = (dataUrl) => {
      const base64 = String(dataUrl).split(",")[1] || "";
      return Math.floor(base64.length * 3 / 4) - (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0);
    };
    const loadImage = (file) => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error(i18n.imageInvalid)); };
      image.src = url;
    });
    const jpegDataUrl = (source, width, height) => {
      const maxDimension = 1600;
      const maxBytes = 1.5 * 1024 * 1024;
      const qualities = [0.84, 0.78, 0.72, 0.68, 0.64];
      let scale = Math.min(1, maxDimension / Math.max(width, height));
      let dataUrl = "";
      for (const quality of qualities) {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const context = canvas.getContext("2d");
        if (!context) throw new Error(i18n.imageInvalid || "Could not read this image.");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(source, 0, 0, canvas.width, canvas.height);
        dataUrl = canvas.toDataURL("image/jpeg", quality);
        if (imageByteLength(dataUrl) <= maxBytes) return dataUrl;
        scale *= 0.82;
      }
      if (dataUrl && imageByteLength(dataUrl) <= 4 * 1024 * 1024) return dataUrl;
      throw new Error(i18n.imageTooLarge || "Image is too large.");
    };
    const prepareHeicImage = async (file) => {
      try {
        const nativeImage = await loadImage(file);
        return jpegDataUrl(nativeImage, nativeImage.naturalWidth, nativeImage.naturalHeight);
      } catch {}

      const libheif = await loadHeicDecoder(heicDecoderUrl);
      const decoder = new libheif.HeifDecoder();
      const images = decoder.decode(new Uint8Array(await file.arrayBuffer()));
      const image = images?.[0];
      if (!image) throw new Error(i18n.imageInvalid || "Could not decode this HEIC image.");
      const width = image.get_width();
      const height = image.get_height();
      if (!width || !height || width * height > 40000000) {
        throw new Error(i18n.imageTooLarge || "Image dimensions are too large.");
      }
      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = width;
      sourceCanvas.height = height;
      const context = sourceCanvas.getContext("2d");
      if (!context) throw new Error(i18n.imageInvalid || "Could not preview this HEIC image.");
      const imageData = context.createImageData(width, height);
      await new Promise((resolve, reject) => image.display(imageData, (displayData) =>
        displayData ? resolve() : reject(new Error(i18n.imageInvalid || "Could not decode this HEIC image."))));
      context.putImageData(imageData, 0, 0);
      try {
        return jpegDataUrl(sourceCanvas, width, height);
      } finally {
        image.free?.();
      }
    };
    const prepareImage = async (file) => {
      if (!isSupportedImage(file)) {
        throw new Error(i18n.imageInvalid || "Use a JPEG, PNG, WebP, HEIC, or HEIF image.");
      }
      if (file.size > 10 * 1024 * 1024) {
        throw new Error(i18n.imageTooLarge || "Image is too large.");
      }
      if (isHeicFile(file)) return prepareHeicImage(file);
      const image = await loadImage(file);
      return jpegDataUrl(image, image.naturalWidth, image.naturalHeight);
    };
    const appendAssistantTurn = () => {
      const element = document.createElement("div");
      element.className = "bulk-color__turn bulk-color__turn--assistant";
      const bubble = document.createElement("div");
      bubble.className = "bulk-color__bubble";
      bubble.innerHTML = `<div class="bulk-color__thinking">${escapeHtml(i18n.understanding)}</div>`;
      element.appendChild(bubble);
      transcript.appendChild(element);
      const turn = { element, bubble, rows: [], cart: null, retired: false };
      bindTurnEvents(turn);
      return turn;
    };
    const retireTurn = (turn) => {
      if (!turn) return;
      turn.retired = true;
      turn.bubble.querySelectorAll("button, input").forEach((control) => { control.disabled = true; });
    };
    const pushMessage = (turn, message, type = "") => {
      const messages = turn.bubble.querySelector("[data-bulk-color-messages]");
      if (!messages) return;
      const div = document.createElement("div");
      div.className = `bulk-color__message${type ? ` bulk-color__message--${type}` : ""}`;
      div.textContent = message;
      messages.appendChild(div);
    };
    const rowStatus = (row) => row.needsReview ? "review" : row.variant ? "matched" : "unmatched";
    const renderTurn = (turn, unparsedCount = 0) => {
      const matched = turn.rows.filter((row) => rowStatus(row) === "matched").length;
      const unmatched = turn.rows.filter((row) => rowStatus(row) === "unmatched").length;
      const review = turn.rows.filter((row) => rowStatus(row) === "review").length;
      const totalQuantity = turn.rows.filter((row) => rowStatus(row) === "matched")
        .reduce((sum, row) => sum + Number(row.quantity || 0), 0);
      turn.bubble.innerHTML = `
        ${turn.reply ? `<p class="bulk-color__reply">${escapeHtml(turn.reply)}</p>` : ""}
        <div class="bulk-color__list" data-bulk-color-body role="list"></div>
        <div class="bulk-color__messages" data-bulk-color-messages></div>
        <div class="bulk-color__checkout">
          <div class="bulk-color__summary">${escapeHtml(formatTemplate(i18n.summary,
            { total: turn.rows.length, matched, unmatched, review, quantity: totalQuantity }))}</div>
          <button type="button" class="button button--primary bulk-color__button" data-bulk-color-add>
            ${escapeHtml(i18n.addButton)}
          </button>
          <a class="bulk-color__cart-link" href="${escapeHtml(`${cartRoot()}cart`)}" data-bulk-color-cart hidden>
            ${escapeHtml(i18n.viewCart)}
          </a>
        </div>`;
      const body = turn.bubble.querySelector("[data-bulk-color-body]");
      turn.rows.forEach((row, index) => {
        const status = rowStatus(row);
        const item = document.createElement("div");
        item.className = "bulk-color__item";
        item.setAttribute("role", "listitem");
        const statusLabel = status === "matched" ? i18n.matched : status === "review" ? i18n.needsReview : i18n.unmatched;
        const changeLabel = formatTemplate(i18n.quantityChange || "{from} → {to}", {
          from: row.currentQuantity || 0, to: Number(row.quantity || 0)
        });
        const visibleStatus = status === "matched" ? `${statusLabel} ${changeLabel}` : statusLabel;
        item.innerHTML = `
          <div class="bulk-color__item-main">
            <div class="bulk-color__code">${escapeHtml(row.colorCode)}</div>
            <span class="bulk-color__status bulk-color__status--${status}" title="${escapeHtml(visibleStatus)}">${escapeHtml(visibleStatus)}</span>
          </div>
          <div class="bulk-color__item-actions">
            <div class="bulk-color__quantity">
              <button type="button" class="bulk-color__quantity-button" data-row-step="${index}" data-step="-1" aria-label="Decrease ${escapeHtml(row.colorCode)} quantity" ${status === "review" ? "disabled" : ""}>−</button>
              <input class="bulk-color__qty" type="number" min="0" step="1" value="${Number(row.quantity) || 0}" data-row-qty="${index}" ${status === "review" ? "disabled" : ""}>
              <button type="button" class="bulk-color__quantity-button" data-row-step="${index}" data-step="1" aria-label="Increase ${escapeHtml(row.colorCode)} quantity" ${status === "review" ? "disabled" : ""}>+</button>
            </div>
            <button type="button" class="bulk-color__remove" data-row-remove="${index}" aria-label="${escapeHtml(i18n.removed)}" title="${escapeHtml(i18n.removed)}"><span aria-hidden="true">×</span></button>
          </div>`;
        body.appendChild(item);
        if (status === "review") {
          const reason = row.reason && i18n[row.reason] ? i18n[row.reason] : i18n.quantityInvalid;
          pushMessage(turn, `${row.colorCode}: ${reason}`);
        }
      });
      const applyButton = turn.bubble.querySelector("[data-bulk-color-add]");
      const hasCartChanges = turn.rows.some((row) => rowStatus(row) === "matched" &&
        row.quantity !== row.currentQuantity);
      applyButton.disabled = turn.retired || !hasCartChanges;
      turn.bubble.querySelector(".bulk-color__checkout").hidden = !hasCartChanges;
      if (unparsedCount > 0) pushMessage(turn, formatTemplate(i18n.aiUnparsed, { count: unparsedCount }));
      if (!turn.rows.length) {
        if (!turn.reply) pushMessage(turn, i18n.unrecognized || i18n.noPurchasable, "error");
      }
      scrollToLatest();
    };
    const showTurnError = (turn, message) => {
      turn.bubble.innerHTML = `<div class="bulk-color__messages" data-bulk-color-messages></div>`;
      pushMessage(turn, message, "error");
      scrollToLatest();
    };
    const showTurnReply = (turn, message) => {
      turn.bubble.innerHTML = `<p class="bulk-color__reply">${escapeHtml(message)}</p>`;
      scrollToLatest();
    };
    const waitForTurnstile = () => new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const check = () => {
        if (window.turnstile) return resolve(window.turnstile);
        if (Date.now() - startedAt >= 10000) return reject(new Error("Turnstile did not load."));
        window.setTimeout(check, 100);
      };
      check();
    });
    const renderTurnstile = async (appearance, timeoutMs) => {
      const turnstile = await waitForTurnstile();
      return new Promise((resolve, reject) => {
        const generation = ++turnstileGeneration;
        let settled = false;
        const timeout = window.setTimeout(() => {
          if (generation === turnstileGeneration) root.classList.remove("bulk-color--turnstile-visible");
          if (!settled) reject(new Error("Turnstile verification timed out."));
          settled = true;
        }, timeoutMs);
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          callback(value);
        };
        if (turnstileWidgetId !== null) turnstile.remove(turnstileWidgetId);
        turnstileWidgetId = null;
        turnstileToken = "";
        root.classList.toggle("bulk-color--turnstile-visible", appearance === "always");
        turnstileWidgetId = turnstile.render(turnstileContainer, {
          sitekey: turnstileSiteKey,
          action: "bulk_color_ai",
          execution: "render",
          appearance,
          size: "flexible",
          retry: "auto",
          "retry-interval": 3000,
          callback: (token) => {
            if (generation !== turnstileGeneration) return;
            turnstileToken = token;
            root.classList.remove("bulk-color--turnstile-visible");
            finish(resolve, token);
          },
          "error-callback": (code) => {
            if (generation !== turnstileGeneration) return;
            root.classList.remove("bulk-color--turnstile-visible");
            finish(reject, new Error(`Turnstile failed (${code || "unknown"}).`));
          },
          "expired-callback": () => { if (generation === turnstileGeneration) turnstileToken = ""; },
          "timeout-callback": () => finish(reject, new Error("Turnstile interaction timed out.")),
          "unsupported-callback": () => finish(reject, new Error("Turnstile does not support this browser."))
        });
        if (turnstileWidgetId == null) finish(reject, new Error("Turnstile widget could not be created."));
      });
    };
    const primeTurnstile = () => {
      if (!protectedRequestEnabled || !turnstileEnabled || turnstileToken || turnstilePrime) return;
      const prime = renderTurnstile("interaction-only", 30000)
        .catch((error) => {
          console.warn("[BulkColorAddToCart] Turnstile preflight did not complete.", error);
          return "";
        });
      turnstilePrime = prime;
      prime.finally(() => { if (turnstilePrime === prime) turnstilePrime = null; });
    };
    const consumeTurnstileToken = () => {
      const token = turnstileToken;
      turnstileToken = "";
      return token;
    };
    const getTurnstileToken = async () => {
      if (!turnstileEnabled) return "";
      if (turnstileToken) return consumeTurnstileToken();
      if (turnstilePrime) {
        await Promise.race([
          turnstilePrime,
          new Promise((resolve) => window.setTimeout(resolve, 5000))
        ]);
        if (turnstileToken) return consumeTurnstileToken();
      }
      turnstilePrime = null;
      await renderTurnstile("always", 120000);
      return consumeTurnstileToken();
    };
    const aiParse = async (text) => {
      aiController?.abort();
      aiController = new AbortController();
      const turnstileToken = await getTurnstileToken();
      try {
        const requestBody = { text };
        if (turnstileEnabled) requestBody.turnstile_token = turnstileToken;
        const response = await fetch(aiParseEndpoint, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(requestBody),
          signal: aiController.signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.status !== "ok" || !payload?.data) {
          throw new Error(payload?.errors?.[0]?.message || `AI request failed with status ${response.status}.`);
        }
        return parseAiCommands(payload.data, validKeys);
      } finally {
        window.setTimeout(primeTurnstile, 0);
      }
    };
    const chatParse = async (text, cart) => {
      if (!chatEndpoint) throw new Error(i18n.aiError || "Conversation service is unavailable.");
      aiController?.abort();
      aiController = new AbortController();
      const turnstileToken = await getTurnstileToken();
      try {
        const requestBody = {
          message: text,
          locale: document.documentElement.lang || window.Shopify?.locale || "en",
          recent_messages: conversationHistory.slice(-10),
          active_order: activeOrderState,
          cart_summary: cartSummary(cart),
          has_pending_order: hasPendingOrder,
          page_context: { page_type: "bulk_bead_order" }
        };
        if (turnstileEnabled) requestBody.turnstile_token = turnstileToken;
        const response = await fetch(chatEndpoint, {
          method: "POST", mode: "cors", credentials: "omit",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(requestBody), signal: aiController.signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.status !== "ok" || !payload?.data) {
          throw new Error(payload?.errors?.[0]?.message || `Conversation request failed with status ${response.status}.`);
        }
        const parsed = parseAiCommands(payload.data, validKeys);
        parsed.reply = String(payload.data.reply || "").trim();
        parsed.activeOrder = Array.isArray(payload.data.active_order) ? payload.data.active_order : [];
        parsed.requiresConfirmation = payload.data.requires_confirmation === true;
        rememberConversation(text, parsed.reply);
        return parsed;
      } finally {
        window.setTimeout(primeTurnstile, 0);
      }
    };
    const ocrParse = async (imageDataUrl, text) => {
      if (!ocrEndpoint) throw new Error(i18n.imageError || "Image recognition is unavailable.");
      aiController?.abort();
      aiController = new AbortController();
      const turnstileToken = await getTurnstileToken();
      const requestStartedAt = performance.now();
      try {
        const requestBody = { image_data_url: imageDataUrl };
        if (turnstileEnabled) requestBody.turnstile_token = turnstileToken;
        const response = await fetch(ocrEndpoint, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(requestBody),
          signal: aiController.signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.status !== "ok" || !payload?.data) {
          throw new Error(payload?.errors?.[0]?.message || `Image request failed with status ${response.status}.`);
        }
        const ocr = payload.data.ocr || payload.data;
        const rawText = String(ocr?.raw_text || "").trim();
        const parserInput = [text, rawText].filter(Boolean).join("\n");
        console.info("[BulkColorAddToCart] OCR completed.", {
          request_ms: Math.round(performance.now() - requestStartedAt),
          image_bytes: imageByteLength(imageDataUrl),
          text_length: rawText.length
        });
        if (!parserInput) {
          return { intent: "ambiguous", commands: [], review: [],
            unparsed: [{ raw_text: "", reason: "ocr_empty", confidence: 0 }] };
        }
        const deterministic = parseCartCommands(parserInput, packSize, validKeys);
        const hasDeterministicResult = deterministic.commands.length > 0 || deterministic.review.length > 0;
        const uncertainBlocks = (Array.isArray(ocr?.text_blocks) ? ocr.text_blocks : []).filter((block) =>
          Number(block?.confidence) < 0.75 || (Array.isArray(block?.warnings) && block.warnings.length > 0));
        const uncertainKeys = new Set();
        uncertainBlocks.forEach((block) => {
          const parsedBlock = parseCartCommands(String(block?.text || ""), packSize, validKeys);
          [...parsedBlock.commands, ...parsedBlock.review].forEach((item) => uncertainKeys.add(item.key));
        });
        if (uncertainKeys.size) {
          const uncertainCommands = deterministic.commands.filter((item) => uncertainKeys.has(item.key))
            .map((item) => ({ ...item, needsReview: true, reason: "aiNeedsReview" }));
          deterministic.commands = deterministic.commands.filter((item) => !uncertainKeys.has(item.key));
          deterministic.review = [...deterministic.review, ...uncertainCommands];
        }
        const userTextNeedsAi = Boolean(text && containsNaturalLanguage(text));
        const useAi = aiEnabled && (!hasDeterministicResult || userTextNeedsAi);
        console.info("[BulkColorAddToCart] OCR parsing decision.", {
          deterministic_commands: deterministic.commands.length,
          review_items: deterministic.review.length,
          uncertain_blocks: uncertainBlocks.length,
          ai_fallback: useAi,
          fallback_reason: !hasDeterministicResult ? "no_rule_match" : userTextNeedsAi ? "user_instruction" : "none"
        });
        if (useAi) {
          primeTurnstile();
          return aiParse(parserInput);
        }
        return deterministic;
      } finally {
        window.setTimeout(primeTurnstile, 0);
      }
    };
    const applyCartChanges = async (turn) => {
      if (inFlight) return;
      const changedRows = turn.rows.filter((row) => rowStatus(row) === "matched" && row.variant?.variantId &&
        row.quantity !== row.currentQuantity);
      if (!changedRows.length) {
        pushMessage(turn, i18n.noPurchasable, "error");
        return;
      }
      const applyButton = turn.bubble.querySelector("[data-bulk-color-add]");
      const messages = turn.bubble.querySelector("[data-bulk-color-messages]");
      inFlight = true;
      updateSendState();
      applyButton.disabled = true;
      messages.innerHTML = "";
      pushMessage(turn, i18n.adding);
      try {
        const freshCart = await getCart();
        if (changedRows.some((row) => cartQuantityForVariant(freshCart, row.variant.variantId) !== row.currentQuantity)) {
          throw Object.assign(new Error("Cart changed"), { code: "CART_CHANGED" });
        }
        const updatedCart = await updateCart(buildCartUpdates(changedRows, freshCart), {
          inputMethod: turn.inputMethod || "color_list",
          refillSet: turn.refillSet || ""
        });
        renderDawnCartUi(updatedCart);
        publishDawnCartUpdate(updatedCart);
        messages.innerHTML = "";
        pushMessage(turn, formatTemplate(i18n.added, { count: changedRows.length }), "success");
        turn.bubble.querySelector("[data-bulk-color-cart]").hidden = false;
        activeOrderState = [];
        hasPendingOrder = false;
        retireTurn(turn);
        if (activeTurn === turn) activeTurn = null;
      } catch (error) {
        messages.innerHTML = "";
        pushMessage(turn, error?.code === "CART_CHANGED" ? i18n.cartChanged :
          error instanceof Error ? error.message : i18n.addFailed, "error");
        applyButton.disabled = false;
      } finally {
        inFlight = false;
        updateSendState();
      }
    };

    function bindTurnEvents(turn) {
      turn.bubble.addEventListener("change", (event) => {
        const target = event.target.closest("[data-row-qty]");
        if (!target || turn.retired) return;
        const index = Number(target.dataset.rowQty);
        const quantity = Number(target.value);
        if (turn.rows[index] && Number.isInteger(quantity) && quantity >= 0) {
          turn.rows[index].quantity = quantity;
          setPendingOrderFromRows(turn.rows, turn.cart);
          renderTurn(turn);
        }
      });
      turn.bubble.addEventListener("click", (event) => {
        if (turn.retired) return;
        const stepTarget = event.target.closest("[data-row-step]");
        if (stepTarget) {
          const index = Number(stepTarget.dataset.rowStep);
          const delta = Number(stepTarget.dataset.step);
          if (turn.rows[index] && Number.isInteger(delta)) {
            turn.rows[index].quantity = Math.max(0, Number(turn.rows[index].quantity || 0) + delta);
            setPendingOrderFromRows(turn.rows, turn.cart);
            renderTurn(turn);
          }
          return;
        }
        const removeTarget = event.target.closest("[data-row-remove]");
        if (removeTarget) {
          turn.rows.splice(Number(removeTarget.dataset.rowRemove), 1);
          setPendingOrderFromRows(turn.rows, turn.cart);
          renderTurn(turn);
          return;
        }
        if (event.target.closest("[data-bulk-color-add]")) applyCartChanges(turn);
      });
    }

    const loadRefillSet = async (setId) => {
      if (inFlight) return;
      const preset = refillSets.find((item) => item.id === setId);
      if (!preset) return;
      refillDialog?.close();
      chat.classList.remove("bulk-color__chat--empty");
      retireTurn(activeTurn);
      appendUserTurn(formatTemplate(i18n.refillUserMessage, { count: preset.count }));
      const turn = appendAssistantTurn();
      turn.inputMethod = "refill_set";
      turn.refillSet = preset.id;
      activeTurn = turn;
      inFlight = true;
      updateSendState();
      root.setAttribute("aria-busy", "true");
      scrollToLatest();
      try {
        const missing = preset.codes.filter((code) => !validKeys.has(code));
        if (missing.length) {
          throw new Error(formatTemplate(i18n.refillUnavailable, { count: missing.length }));
        }
        const cart = await getCart();
        turn.cart = cart;
        turn.reply = formatTemplate(i18n.refillLoaded, { count: preset.count });
        turn.rows = planCartCommands({ commands: preset.codes.map((key) => ({
          key, colorCode: key, quantity: 1, action: "add", sourceText: preset.id
        })), review: [] }, variantIndex, cart).rows;
        setPendingOrderFromRows(turn.rows, cart);
        renderTurn(turn);
      } catch (error) {
        showTurnError(turn, error instanceof Error ? error.message : i18n.addFailed);
      } finally {
        inFlight = false;
        updateSendState();
        root.removeAttribute("aria-busy");
      }
    };

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const text = input.value.trim();
      const submittedImage = selectedImage;
      if ((!text && !submittedImage) || inFlight) return;
      chat.classList.remove("bulk-color__chat--empty");
      retireTurn(activeTurn);
      appendUserTurn(text, submittedImage?.previewUrl || "");
      const turn = appendAssistantTurn();
      turn.inputMethod = submittedImage ? "photo" : "color_list";
      activeTurn = turn;
      input.value = "";
      selectedImage = null;
      imageInput.value = "";
      renderSelectedImage();
      resizeInput();
      inFlight = true;
      updateSendState();
      root.setAttribute("aria-busy", "true");
      scrollToLatest();
      let stage = "parse";
      try {
        let parsed;
        let cart = null;
        if (submittedImage) {
          stage = "ocr";
          parsed = await ocrParse(submittedImage.imageDataUrl, text);
        } else {
          const deterministic = parseCartCommands(text, packSize, validKeys);
          const hasDeterministicResult = deterministic.commands.length > 0 || deterministic.review.length > 0;
          const useConversation = chatEnabled &&
            (hasPendingOrder || !hasDeterministicResult || containsNaturalLanguage(text) || /[?？]/.test(text));
          const useAi = !useConversation && aiEnabled && (!hasDeterministicResult || containsNaturalLanguage(text));
          if (useConversation) {
            stage = "cart";
            cart = await getCart();
            stage = "ai";
            parsed = await chatParse(text, cart);
          } else {
            if (useAi) stage = "ai";
            parsed = useAi ? await aiParse(text) : deterministic;
          }
        }
        if (parsed.intent === "off_topic") {
          showTurnReply(turn, parsed.reply || i18n.offTopic || i18n.unrecognized);
          return;
        }
        stage = "cart";
        cart ||= await getCart();
        turn.cart = cart;
        turn.reply = parsed.reply || "";
        turn.rows = planCartCommands(parsed, variantIndex, cart).rows;
        if ((parsed.intent === "cart_command" || !parsed.intent) && turn.rows.length) {
          setPendingOrderFromRows(turn.rows, cart);
        } else if (Array.isArray(parsed.activeOrder)) {
          activeOrderState = parsed.activeOrder;
        }
        renderTurn(turn, parsed.unparsed?.length || 0);
      } catch (error) {
        if (error?.name !== "AbortError") {
          const verificationError = error instanceof Error && error.message.startsWith("Turnstile");
          const message = verificationError ? error.message : stage === "ocr" ?
            (error instanceof Error ? error.message : i18n.imageError) :
            stage === "ai" ? i18n.aiError : stage === "cart" ? i18n.cartReadError :
            error instanceof Error ? error.message : i18n.addFailed;
          showTurnError(turn, message || i18n.addFailed);
          console.warn("[BulkColorAddToCart] Request failed.", error);
        }
      } finally {
        aiController = null;
        inFlight = false;
        updateSendState();
        root.removeAttribute("aria-busy");
        input.focus();
      }
    });
    input.addEventListener("input", () => {
      resizeInput();
      updateSendState();
    });
    attachButton.addEventListener("click", () => imageInput.click());
    imageInput.addEventListener("change", async () => {
      const file = imageInput.files?.[0];
      if (!file) return;
      if (!isSupportedImage(file)) {
        imageInput.value = "";
        window.alert(i18n.imageInvalid || "Use a JPEG, PNG, WebP, HEIC, or HEIF image.");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        imageInput.value = "";
        window.alert(i18n.imageTooLarge || "Image is too large.");
        return;
      }
      imagePreparing = true;
      updateSendState();
      try {
        const prepareStartedAt = performance.now();
        const imageDataUrl = await prepareImage(file);
        if (selectedImage?.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(selectedImage.previewUrl);
        selectedImage = { previewUrl: imageDataUrl, imageDataUrl };
        console.info("[BulkColorAddToCart] Image prepared.", {
          prepare_ms: Math.round(performance.now() - prepareStartedAt),
          original_bytes: file.size,
          upload_bytes: imageByteLength(imageDataUrl)
        });
        renderSelectedImage();
        input.focus();
      } catch (error) {
        imageInput.value = "";
        window.alert(error instanceof Error ? error.message : i18n.imageInvalid);
      } finally {
        imagePreparing = false;
        updateSendState();
      }
    });
    imageRemove.addEventListener("click", () => clearSelectedImage());
    if (refillOpen && refillDialog && refillClose && refillSets.length) {
      refillOpen.addEventListener("click", () => refillDialog.showModal());
      refillClose.addEventListener("click", () => refillDialog.close());
      refillDialog.addEventListener("click", (event) => {
        if (event.target === refillDialog) refillDialog.close();
        const option = event.target.closest("[data-bulk-color-refill-set]");
        if (option) loadRefillSet(option.dataset.bulkColorRefillSet);
      });
      refillDialog.addEventListener("cancel", () => refillDialog.close());
    } else if (refillOpen) refillOpen.hidden = true;
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    resizeInput();
    updateSendState();
    primeTurnstile();
  };

  const initAllBulkColorSections = (container = document) =>
    container.querySelectorAll("[data-bulk-color-root]").forEach(initBulkColor);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAllBulkColorSections());
  else initAllBulkColorSections();
  document.addEventListener("shopify:section:load", (event) => initAllBulkColorSections(event.target));
})();
